import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiUser, FiMail, FiCalendar, FiClock, FiFileText, FiCheck, FiZap, FiType } = FiIcons;

const SpeakerProposalModal = ({ isOpen, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: '',
    topic: 'Wellness',
    date: '',
    time: '',
    bio: '',
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
        setFormData({ name: '', email: '', title: '', topic: 'Wellness', date: '', time: '', bio: '', description: '' });
      }, 2500);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl my-8 overflow-hidden border border-brand-dark"
      >
        <div className="p-8 md:p-10 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-navy text-white rounded-2xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiZap} className="w-6 h-6 text-accent-gold" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Speaker Proposal</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Share your knowledge with us</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 md:p-10">
          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.form key="proposal-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Full Name</label>
                    <div className="relative group">
                      <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold" />
                      <input required className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all" placeholder="e.g. Dr. Jane Smith" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Email Address</label>
                    <div className="relative group">
                      <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold" />
                      <input required type="email" className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all" placeholder="jane@example.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Proposed Title</label>
                    <div className="relative group">
                      <SafeIcon icon={FiType} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold" />
                      <input required className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all" placeholder="The Power of Shared Memory" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Topic Category</label>
                    <select className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm appearance-none cursor-pointer" value={formData.topic} onChange={e => setFormData({...formData, topic: e.target.value})}>
                      <option>Writing</option>
                      <option>Legal</option>
                      <option>Wellness</option>
                      <option>Planning</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Proposed Date</label>
                    <input required type="date" className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Proposed Time</label>
                    <input required type="time" className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-sm transition-all" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Speaker Biography</label>
                  <textarea required className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none h-24 resize-none text-sm leading-relaxed" placeholder="Tell us about your background and expertise..." value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})} />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Session Description</label>
                  <textarea required className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none h-32 resize-none text-sm leading-relaxed" placeholder="What will participants learn in this session?" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full btn-primary py-5 text-lg flex items-center justify-center gap-3 shadow-xl transform active:scale-95 transition-all">
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Submit Proposal <SafeIcon icon={FiCheck} /></>
                  )}
                </button>
              </motion.form>
            ) : (
              <motion.div key="proposal-success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-12 text-center">
                <div className="w-24 h-24 bg-green-50 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                  <SafeIcon icon={FiCheck} className="w-12 h-12" />
                </div>
                <h4 className="text-3xl font-serif text-accent-navy mb-4">Proposal Received</h4>
                <p className="text-sm text-text-muted font-light leading-relaxed mb-8 max-w-sm mx-auto">
                  Thank you for offering your expertise to our community. Our curation team will review your proposal and contact you within 5 business days.
                </p>
                <div className="bg-brand-light p-6 rounded-3xl border border-brand-dark flex items-center gap-4 text-left max-w-md mx-auto">
                  <SafeIcon icon={FiCalendar} className="text-accent-gold w-6 h-6" />
                  <p className="text-[11px] text-text-muted leading-relaxed">
                    A copy of your submission has been sent to <span className="font-bold text-accent-navy">{formData.email}</span> for your records.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default SpeakerProposalModal;